import get from 'lodash/get';

import { WebEDIRowData } from 'support/types/webediTable';

import { DisplayModes } from './types';

const MODIFICATION_GROUPS_KEY = 'modification_groups';
const ALLOWANCES_KEY = 'allowances';
const CHARGES_KEY = 'charges';
const RELATIVE_TYPE = 'RELATIVE';
const TYPE_FIELD = 'type';
const PERCENTAGE_FIELD = 'percentage';
const AMOUNT_FIELD = 'amount';

const MG_ESCAPED = MODIFICATION_GROUPS_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ALLOWANCES_CHARGES_GROUP = `(${ALLOWANCES_KEY}|${CHARGES_KEY})`;

const DOT_NOTATION_REGEX = new RegExp(`${MG_ESCAPED}\\.(\\d+)\\.${ALLOWANCES_CHARGES_GROUP}\\.(\\d+)`);
const BRACKET_NOTATION_REGEX = new RegExp(`${MG_ESCAPED}\\[(\\d+)\\]\\.${ALLOWANCES_CHARGES_GROUP}\\.\\*`);
const BRACKET_TO_DOT_REGEX = new RegExp(`${MG_ESCAPED}\\[(\\d+)\\]\\.`);
const WILDCARD_SUFFIX_REGEX = /\.\*\..*$/;
const AMOUNT_SUFFIX_REGEX = new RegExp(`${AMOUNT_FIELD}$`);

type ModificationPath = {
  type: 'allowances' | 'charges';
  basePath: string;
};

export const extractModificationPath = (fieldKey: string): ModificationPath | null => {
  const dotNotationMatch = fieldKey.match(DOT_NOTATION_REGEX);
  if (dotNotationMatch) {
    return {
      type: dotNotationMatch[2] as 'allowances' | 'charges',
      basePath: dotNotationMatch[0],
    };
  }

  const bracketNotationMatch = fieldKey.match(BRACKET_NOTATION_REGEX);
  if (bracketNotationMatch) {
    const index = bracketNotationMatch[1];
    const type = bracketNotationMatch[2] as 'allowances' | 'charges';
    return {
      type,
      basePath: `${MODIFICATION_GROUPS_KEY}.${index}.${type}.*`,
    };
  }

  return null;
};

export const isRelativeModificationType = (typeValue: unknown): boolean => {
  return typeValue === RELATIVE_TYPE || !typeValue;
};

export const getModificationFieldKey = (originalKey: string, isRelative: boolean): string => {
  if (!isRelative) {
    return originalKey;
  }

  if (originalKey.endsWith(AMOUNT_FIELD)) {
    return originalKey.replace(AMOUNT_SUFFIX_REGEX, PERCENTAGE_FIELD);
  }

  return originalKey;
};

export const isRelativeModificationForWebEDI = (
  fieldId: string | undefined,
  rowData: WebEDIRowData,
  mode: DisplayModes,
  isLineItems: boolean,
): boolean => {
  const isViewMode = mode === DisplayModes.view;
  if (!fieldId || !rowData) {
    return false;
  }

  if (!isLineItems) {
    return (
      typeof rowData === 'object' &&
      Boolean(
        (TYPE_FIELD in rowData && isRelativeModificationType(rowData.type)) ||
        (isViewMode && PERCENTAGE_FIELD in rowData && rowData.percentage),
      )
    );
  }

  const pathInfo = extractModificationPath(fieldId);
  if (!pathInfo) {
    return false;
  }

  const typeValue = get(rowData, `${pathInfo.basePath}.${TYPE_FIELD}`);
  return (
    isRelativeModificationType(typeValue) ||
    (isViewMode && Boolean(get(rowData, `${pathInfo.basePath}.${PERCENTAGE_FIELD}`)))
  );
};

export const isRelativeModificationForExcel = (columnKey: string, excelRow: Record<string, unknown>): boolean => {
  const pathInfo = extractModificationPath(columnKey);
  if (!pathInfo) {
    return false;
  }

  const typeKey = columnKey
    .replace(BRACKET_TO_DOT_REGEX, `${MODIFICATION_GROUPS_KEY}[$1].`)
    .replace(WILDCARD_SUFFIX_REGEX, `.*.${TYPE_FIELD}`);
  const typeValue = excelRow[typeKey];

  return isRelativeModificationType(typeValue);
};
